import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Swiper from 'swiper';
import { Navigation, Scrollbar, FreeMode, A11y } from 'swiper/modules';
import { sliderConfig } from '@utils';
import { feature as topojsonFeature } from 'topojson-client';

let countriesGeojsonPromise = null;
let worldMapInstanceId = 0;

function loadCountriesGeojson() {
  if (!countriesGeojsonPromise) {
    countriesGeojsonPromise = import('./world-map-countries.json').then(({ default: topology }) =>
      topojsonFeature(topology, topology.objects[Object.keys(topology.objects)[0]]),
    );
  }

  return countriesGeojsonPromise;
}

function createWorldMapInstance() {
  const focusFilterId = `au-world-map-focus-ring-${++worldMapInstanceId}`;
  let map = null;
  let baseLayer = null;
  let worldData = null;
  let countriesGeojson = null;
  let rootEl = null;
  let othersSelectObserver = null;
  let isSyncingOthersSelect = false;
  let syncOthersSelectTimer = null;
  let activeCountryLayers = new Map();
  let popupOriginCountryIso = '';
  let pendingCountryFocusIso = '';
  let filtersSwiper = null;
  let reliefLayer = null;
  let reliefFallbackApplied = false;
  let resizeHandler = null;
  let outsidePointerDownHandler = null;
  let escapeKeyHandler = null;
  let toastTimer = null;
  let selectedCountriesBoundsCache = null;

  let selectedCountries = new Set();
  let selectedCategories = new Set();
  let selectedColor = '#18c8c4';
  let region = null;
  let categories = [];
  let countryCategories = {};
  let countryCards = {};
  let countryNameToIso = new Map();
  let categoryByKey = new Map();
  let labelToKey = new Map();
  let reliefEnabled = false;

  // Mapping ISO2 → ISO3 para países con ISO_A3 = "-99" en el GeoJSON
  const ISO2_TO_ISO3 = {
    FR: 'FRA',
    NO: 'NOR',
    AX: 'ALA',
    BV: 'BVT',
    GS: 'SGS',
    HM: 'HMD',
    SJ: 'SJM',
    UM: 'UMI',
  };

  const POPUP_TOP_PADDING = 110;
  const POPUP_SIDE_PADDING = 24;
  const RELIEF_PANE_NAME = 'au-world-map-relief-pane';
  const RELIEF_PANE_FILTER = 'blur(1.2px) saturate(0.78) brightness(1.08) contrast(0.74)';
  const RELIEF_TILE_URL = '/images/modules/Maps/world-relief/{z}/{x}/{y}.png';
  const RELIEF_TILE_ATTRIBUTION = 'Relief tiles local';
  const RELIEF_TILE_FALLBACK_URL =
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Physical_Map/MapServer/tile/{z}/{y}/{x}';
  const RELIEF_TILE_FALLBACK_ATTRIBUTION = 'Tiles &copy; Esri &mdash; Source: USGS, Esri, and Garmin';
  const RELIEF_TILE_OPACITY = 0.9;
  const RELIEF_STROKE_COLOR = '#6f7d74';
  const RELIEF_STROKE_HOVER_COLOR = '#5f6e65';
  const RELIEF_STROKE_WEIGHT = 1.05;
  const RELIEF_STROKE_HOVER_WEIGHT = 1.25;
  const WORLD_MAP_BOUNDS = [
    [-85, -180],
    [85, 180],
  ];
  const DEFAULT_REGIONS = {
    world: {
      bounds: [
        [-55, -180],
        [85, 180],
      ],
      maxZoom: 3,
    },
    europe: {
      bounds: [
        [34, -25],
        [72, 45],
      ],
      maxZoom: 5,
    },
    latam: {
      bounds: [
        [-60, -120],
        [35, -30],
      ],
      maxZoom: 4,
    },
    northAmerica: {
      bounds: [
        [15, -170],
        [75, -50],
      ],
      maxZoom: 4,
    },
    asia: {
      bounds: [
        [5, 40],
        [75, 180],
      ],
      maxZoom: 4,
    },
  };
  let regions = { ...DEFAULT_REGIONS };

  const DEFAULT_TEXTS = {
    cardCloseLabel: 'Cerrar',
    cardLinkLabel: 'Ver más sobre {title}',
    cardTagsLabel: 'Etiquetas del país',
    cardTagsOverflowLabel: 'Ver {count} tags adicionales',
    cardDefaultTitle: 'País',
    cardDefaultDescription: 'Sin descripción disponible.',
    activeCountryFallbackLabel: 'País activo',
    clearFiltersLabel: 'Limpiar todos los filtros de categoría',
    locateSuccess: 'Mapa centrado en tu ubicación.',
    noResultsTitle: 'No se han encontrado resultados que coincidan con tu búsqueda.',
    noResultsDetail: 'Por favor, revisa los filtros seleccionados o prueba con una nueva búsqueda.',
  };
  let texts = { ...DEFAULT_TEXTS };

  const inRoot = (selector) => (rootEl ? rootEl.querySelector(selector) : null);
  const toastEl = () => inRoot('[data-au-world-map-toast]');
  const filtersEl = () => inRoot('[data-au-world-map-category-buttons]');
  const othersSelectEl = () => inRoot('[data-au-world-map-others-select]');
  const clearFiltersSlideEl = () => inRoot('[data-au-world-map-clear-filters-slide]');
  const clearFiltersChipEl = () => inRoot('[data-au-world-map-clear-filters]');
  const controlsEl = () => inRoot('.au-world-map__controls');

  function getModuleTitle() {
    const titleElement = inRoot('[data-au-heading-title]');
    return String(titleElement?.textContent || '').trim();
  }

  function getCategoryLabel(categoryKey) {
    return categoryByKey.get(categoryKey)?.label || categoryKey;
  }

  function pushCategoryFilterDataLayer(categoryKey, action) {
    const titleText = getModuleTitle();
    const clickText = getCategoryLabel(categoryKey);

    if (!titleText || !clickText || !action) return;

    window.dataLayer?.push({
      event: 'navigation',
      content_type: 'clickFiltro',
      item_id: `${titleText}_${clickText}_${action}`,
    });
  }

  function pushControlButtonDataLayer(controlSuffix) {
    const titleText = getModuleTitle();
    if (!titleText || !controlSuffix) return;

    window.dataLayer?.push({
      event: 'navigation',
      content_type: 'clickBoton',
      item_id: `${titleText}_${controlSuffix}`,
    });
  }

  function getPopupCardTitle(popupElement) {
    const cardTitleElement = popupElement?.querySelector('.au-world-map__card-title');
    return String(cardTitleElement?.textContent || '').trim();
  }

  function pushPopupCardButtonDataLayer(cardTitle, buttonSuffix) {
    if (!cardTitle || !buttonSuffix) return;

    window.dataLayer?.push({
      event: 'navigation',
      content_type: 'clickBoton',
      item_id: `${cardTitle}_${buttonSuffix}`,
    });
  }

  function normalizeCountryLabelForItemId(value) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, '_');
  }

  function getFeatureTrackingCountryLabel(feature) {
    const cardData = getCardDataForFeature(feature);
    return cardData?.title || getCountryName(feature) || featureToIso(feature);
  }

  function pushCountrySelectionDataLayer(feature) {
    const countryLabel = normalizeCountryLabelForItemId(getFeatureTrackingCountryLabel(feature));
    if (!countryLabel) return;

    window.dataLayer?.push({
      event: 'navigation',
      content_type: 'clickBoton',
      item_id: `mapa_${countryLabel}`,
    });
  }

  function resolveOptionKey(value) {
    const raw = String(value ?? '').trim();
    return labelToKey.get(raw) || raw;
  }

  function getOtherCategoryKeys() {
    const othersSelect = othersSelectEl();
    if (!othersSelect) return [];

    return Array.from(othersSelect.querySelectorAll('[data-au-option]')).map((option) =>
      resolveOptionKey(option.value),
    );
  }

  function syncOthersSelectFromState() {
    const othersSelect = othersSelectEl();
    if (!othersSelect) return;

    if (syncOthersSelectTimer) {
      clearTimeout(syncOthersSelectTimer);
    }

    isSyncingOthersSelect = true;
    const selectedLabels = [];
    const listboxOptions = Array.from(othersSelect.querySelectorAll('[data-au-listbox-option]'));

    Array.from(othersSelect.querySelectorAll('[data-au-option]')).forEach((option, index) => {
      const key = resolveOptionKey(option.value);
      const isSelected = selectedCategories.has(key);
      const listboxOption = listboxOptions[index];

      if (isSelected) {
        if (!option.hasAttribute('selected')) {
          option.setAttribute('selected', true);
        }
        selectedLabels.push(option.value);
      } else if (option.hasAttribute('selected')) {
        option.removeAttribute('selected');
      }

      if (!listboxOption) return;

      listboxOption.classList.toggle('au-active', isSelected);
      if (listboxOption.hasAttribute('aria-selected')) {
        const nextAriaSelected = isSelected ? 'true' : 'false';
        if (listboxOption.getAttribute('aria-selected') !== nextAriaSelected) {
          listboxOption.setAttribute('aria-selected', nextAriaSelected);
        }
      }
    });

    const selectText = othersSelect.querySelector('[data-au-select-text]');
    if (selectText) {
      const nextText = selectedLabels.join(', ');
      if (selectText.textContent !== nextText) {
        selectText.textContent = nextText;
      }
    }

    // MutationObserver callbacks are microtask-based; keep sync guard until next task.
    syncOthersSelectTimer = setTimeout(() => {
      isSyncingOthersSelect = false;
      syncOthersSelectTimer = null;
    }, 0);

    syncOthersSelectMaterialIcon();
  }

  function getSelectedOtherKeysFromOptions() {
    const othersSelect = othersSelectEl();
    if (!othersSelect) return new Set();

    const selectedKeys = new Set();
    const selectedOptions = Array.from(othersSelect.querySelectorAll('[data-au-option][selected]'));

    selectedOptions.forEach((option) => {
      const key = resolveOptionKey(option.value);
      if (key) selectedKeys.add(key);
    });

    return selectedKeys;
  }

  function syncOthersSelectMaterialIcon() {
    const othersSelect = othersSelectEl();
    if (!othersSelect) return;

    const hasSelectedCategories = getSelectedOtherKeysFromOptions().size > 0;
    const selectTrigger = othersSelect.querySelector('[data-au-select-main]');
    if (selectTrigger) {
      selectTrigger.classList.toggle('au-world-map__others-selected', hasSelectedCategories);
    }

    const selectIcon = othersSelect.querySelector('.au-select__icon[data-au-icon]');
    if (!selectIcon) return;

    const nextIcon = hasSelectedCategories ? 'check' : 'more_horiz';
    if (selectIcon.textContent !== nextIcon) {
      selectIcon.textContent = nextIcon;
    }
  }

  function syncSelectedCategoriesFromOthersSelect() {
    const otherKeys = new Set(getOtherCategoryKeys());
    const selectedOtherKeys = getSelectedOtherKeysFromOptions();
    const changedCategories = [];

    otherKeys.forEach((key) => {
      const wasSelected = selectedCategories.has(key);
      const isSelected = selectedOtherKeys.has(key);

      if (isSelected) selectedCategories.add(key);
      else selectedCategories.delete(key);

      if (wasSelected !== isSelected) {
        changedCategories.push({
          key,
          action: isSelected ? 'seleccionar' : 'deseleccionar',
        });
      }
    });

    return changedCategories;
  }

  function updateMapFromOthersSelect() {
    if (isSyncingOthersSelect) return;

    const changedCategories = syncSelectedCategoriesFromOthersSelect();
    changedCategories.forEach(({ key, action }) => {
      pushCategoryFilterDataLayer(key, action);
    });

    renderCategoryButtons();
    rebuildLayers();
  }

  function scheduleMapUpdateFromOthersSelect() {
    requestAnimationFrame(() => {
      updateMapFromOthersSelect();
    });
  }

  function hideOthersSelectListbox(focusTrigger = false) {
    const othersSelect = othersSelectEl();
    if (!othersSelect) return;

    const listbox = othersSelect.querySelector('[data-au-listbox]');
    const listboxOptions = othersSelect.querySelectorAll('[data-au-listbox-option]');
    const selectMain = othersSelect.querySelector('[data-au-select-main]');
    const selectArrow = othersSelect.querySelector('[data-au-float-input-arrow]');

    if (listbox) {
      listbox.classList.add('au-visually-hidden');
    }

    listboxOptions.forEach((listboxOption) => {
      listboxOption.setAttribute('tabindex', '-1');
    });

    othersSelect.classList.remove('au-select--active');
    selectMain?.setAttribute('aria-expanded', 'false');
    selectArrow?.classList.remove('au-float-input__arrow--active');

    if (focusTrigger) {
      selectMain?.focus();
    }
  }

  function bindOthersSelectEvents() {
    const othersSelect = othersSelectEl();
    if (!othersSelect || othersSelect.dataset.worldMapBound === 'true') return;

    othersSelect.dataset.worldMapBound = 'true';

    const getNavigableListboxOptions = () =>
      Array.from(othersSelect.querySelectorAll('[data-au-listbox-option]')).filter(
        (option) => !option.hasAttribute('data-au-listbox-option-disabled'),
      );

    const selectMain = othersSelect.querySelector('[data-au-select-main]');
    if (selectMain && selectMain.dataset.worldMapArrowBound !== 'true') {
      selectMain.dataset.worldMapArrowBound = 'true';
      selectMain.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          requestAnimationFrame(() => {
            const navigableOptions = getNavigableListboxOptions();
            if (!navigableOptions.length) return;

            const activeOption = navigableOptions.find((option) => option.classList.contains('au-active'));
            (activeOption || navigableOptions[0]).focus();
          });
          return;
        }

        if (event.key === 'Tab' && othersSelect.classList.contains('au-select--active')) {
          hideOthersSelectListbox();
        }
      });
    }

    const optionNodes = Array.from(othersSelect.querySelectorAll('[data-au-option]'));
    const listboxOptions = Array.from(othersSelect.querySelectorAll('[data-au-listbox-option]'));

    if (othersSelectObserver) {
      othersSelectObserver.disconnect();
    }

    othersSelectObserver = new MutationObserver((mutations) => {
      const hasRelevantMutation = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'selected' ||
            mutation.attributeName === 'class' ||
            mutation.attributeName === 'aria-selected'),
      );

      if (!hasRelevantMutation || isSyncingOthersSelect) return;

      updateMapFromOthersSelect();
    });

    optionNodes.forEach((optionNode) => {
      othersSelectObserver.observe(optionNode, {
        attributes: true,
        attributeFilter: ['selected'],
      });
    });

    listboxOptions.forEach((listboxOption) => {
      othersSelectObserver.observe(listboxOption, {
        attributes: true,
        attributeFilter: ['class', 'aria-selected'],
      });

      if (listboxOption.dataset.worldMapFilterBound === 'true') return;

      listboxOption.dataset.worldMapFilterBound = 'true';
      listboxOption.addEventListener('click', scheduleMapUpdateFromOthersSelect);
      listboxOption.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();

          const navigableOptions = getNavigableListboxOptions();
          if (!navigableOptions.length) return;

          const currentIndex = navigableOptions.indexOf(listboxOption);
          if (currentIndex === -1) return;

          const direction = event.key === 'ArrowDown' ? 1 : -1;
          const nextIndex = (currentIndex + direction + navigableOptions.length) % navigableOptions.length;
          navigableOptions[nextIndex].focus();
          return;
        }

        if (event.key === 'Tab') {
          hideOthersSelectListbox();
          return;
        }

        if (event.key !== 'Enter' && event.key !== ' ') return;
        scheduleMapUpdateFromOthersSelect();
      });
    });

    if (othersSelect.dataset.worldMapOutsideCloseBound !== 'true') {
      othersSelect.dataset.worldMapOutsideCloseBound = 'true';

      outsidePointerDownHandler = (event) => {
        const target = event.target;
        if (!(target instanceof Node)) return;
        if (!othersSelect.classList.contains('au-select--active')) return;
        if (othersSelect.contains(target)) return;
        hideOthersSelectListbox();
      };

      escapeKeyHandler = (event) => {
        if (event.key !== 'Escape') return;
        if (!othersSelect.classList.contains('au-select--active')) return;
        hideOthersSelectListbox(true);
      };

      document.addEventListener('pointerdown', outsidePointerDownHandler, true);
      document.addEventListener('keydown', escapeKeyHandler);
    }
  }

  function cleanupGlobalListeners() {
    if (outsidePointerDownHandler) {
      document.removeEventListener('pointerdown', outsidePointerDownHandler, true);
      outsidePointerDownHandler = null;
    }

    if (escapeKeyHandler) {
      document.removeEventListener('keydown', escapeKeyHandler);
      escapeKeyHandler = null;
    }

    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }

    if (syncOthersSelectTimer) {
      clearTimeout(syncOthersSelectTimer);
      syncOthersSelectTimer = null;
      isSyncingOthersSelect = false;
    }
  }

  function showToast(message, duration = 2600, isHtml = false) {
    const el = toastEl();
    if (!el) return;

    const applyContent = () => {
      if (isHtml) el.innerHTML = message;
      else el.textContent = message;
    };

    const wasVisible = el.classList.contains('au-show');

    if (wasVisible) {
      el.textContent = '';
      requestAnimationFrame(applyContent);
    } else {
      applyContent();
    }

    el.classList.add('au-show');
    clearTimeout(toastTimer);
    if (duration <= 0) return;
    toastTimer = setTimeout(() => el.classList.remove('au-show'), duration);
  }

  function normalizeText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');
  }

  function sanitizeLink(value) {
    const link = String(value ?? '').trim();
    if (!link) return '#';
    return /^(https?:|\/|#|mailto:|tel:)/i.test(link) ? link : '#';
  }

  function formatText(template, replacements = {}) {
    return String(template ?? '').replace(/\{(\w+)\}/g, (match, key) =>
      Object.prototype.hasOwnProperty.call(replacements, key) ? String(replacements[key]) : match,
    );
  }

  function getProps(feature) {
    return feature && feature.properties ? feature.properties : {};
  }

  function getIsoA2(feature) {
    const p = getProps(feature);
    return String(p.ISO_A2_EH || p.ISO_A2 || p.iso_a2 || '').toUpperCase();
  }

  function getIsoA3(feature) {
    const p = getProps(feature);
    return String(
      p.ISO_A3_EH ||
        p.ISO_A3 ||
        p.iso_a3 ||
        p.ADM0_A3 ||
        p.adm0_a3 ||
        p.ISO_A2_EH ||
        p.ISO_A2 ||
        p.iso_a2 ||
        feature.id ||
        '',
    ).toUpperCase();
  }

  function getCountryName(feature) {
    const p = getProps(feature);
    return String(
      p.ADMIN ||
        p.admin ||
        p.NAME ||
        p.name ||
        p.SOVEREIGNT ||
        p.sovereignt ||
        p.formal_en ||
        p.brk_name ||
        p.formal_fr ||
        '',
    );
  }

  function buildCountryMappingFromGeojson() {
    countryNameToIso = new Map();

    if (!countriesGeojson || !countriesGeojson.features) return;

    countriesGeojson.features.forEach((feature) => {
      let iso = getIsoA3(feature);
      const countryName = getCountryName(feature);
      if (!countryName) return;

      // Si ISO_A3 es "-99", intentar resolver usando ISO_A2
      if (iso === '-99') {
        const iso2 = getIsoA2(feature);
        iso = ISO2_TO_ISO3[iso2] || '';
      }

      if (!iso) return;

      const normalized = normalizeText(countryName);
      if (!normalized) return;

      // Mapear el nombre normalizado → ISO3
      if (!countryNameToIso.has(normalized)) {
        countryNameToIso.set(normalized, iso);
      }
    });
  }

  function resolveCountryToIso(value) {
    const raw = normalizeText(value);
    if (!raw) return '';

    // Si es un código ISO3 válido, devolverlo
    if (raw.length === 3 && /^[a-z0-9]{3}$/.test(raw)) return raw.toUpperCase();

    // Mapping dinámico desde GeoJSON
    if (countryNameToIso.has(raw)) return countryNameToIso.get(raw);

    // Fallback: devolver normalizado en mayúsculas
    return raw.toUpperCase();
  }

  function featureToIso(feature) {
    let iso = getIsoA3(feature);

    // Si ISO_A3 es "-99", intentar resolver usando ISO_A2
    if (iso === '-99') {
      const iso2 = getIsoA2(feature);
      iso = ISO2_TO_ISO3[iso2] || '';
    }

    if (iso && iso !== '-99') return iso;
    return resolveCountryToIso(getCountryName(feature));
  }

  function normalizeSelectedCountries(values) {
    return new Set((values || []).map(resolveCountryToIso));
  }

  function toCategoryObject(category) {
    if (category && typeof category === 'object') {
      const key = String(category.key ?? category.label ?? '').trim();
      return { key, label: String(category.label ?? key), icon: category.icon || 'info' };
    }
    const value = String(category ?? '').trim();
    return { key: value, label: value, icon: 'info' };
  }

  function getConfiguredCategories(categoriesArr, countryCategoriesObj) {
    const source =
      Array.isArray(categoriesArr) && categoriesArr.length
        ? categoriesArr
        : Object.values(countryCategoriesObj || {}).flat();
    const unique = [];
    const seen = new Set();

    for (const cat of source) {
      const category = toCategoryObject(cat);
      if (!category.key || seen.has(category.key)) continue;
      seen.add(category.key);
      unique.push(category);
    }

    return unique;
  }

  function rebuildCategoryLookups() {
    categoryByKey = new Map();
    labelToKey = new Map();

    getConfiguredCategories(categories, countryCategories).forEach((category) => {
      categoryByKey.set(category.key, category);
      if (category.label) labelToKey.set(category.label, category.key);
    });
  }

  function getCountryCategories(feature, countryCategoriesObj) {
    const iso = featureToIso(feature);
    const nameKey = normalizeText(getCountryName(feature));
    let values = countryCategoriesObj?.[iso];

    if (!values) {
      for (const [key, list] of Object.entries(countryCategoriesObj || {})) {
        if (normalizeText(key) === nameKey) {
          values = list;
          break;
        }
      }
    }

    return Array.isArray(values) ? values : [];
  }

  function isActiveFeature(feature) {
    const featureIso = featureToIso(feature);
    if (!selectedCountries.has(featureIso)) return false;

    // If no categories selected, all countries in selectedCountries are active
    if (selectedCategories.size === 0) return true;

    // If categories selected, only countries with one of those categories are active
    const featureCats = getCountryCategories(feature, countryCategories);
    return featureCats.some((cat) => selectedCategories.has(cat));
  }

  function getStyleForFeature(feature) {
    const active = isActiveFeature(feature);
    const inJson = selectedCountries.has(featureToIso(feature));

    if (reliefEnabled) {
      return {
        color: RELIEF_STROKE_COLOR,
        weight: RELIEF_STROKE_WEIGHT,
        opacity: 1,
        fillColor: 'transparent',
        fillOpacity: 0,
        lineJoin: 'round',
      };
    }

    if (!inJson) {
      return {
        color: '#ffffff',
        weight: 0.7,
        opacity: 1,
        fillColor: '#ffffff',
        fillOpacity: 1,
        lineJoin: 'round',
      };
    }

    if (!active) {
      return {
        color: '#ffffff',
        weight: 0.8,
        opacity: 1,
        fillColor: '#9bcbe3',
        fillOpacity: 1,
        lineJoin: 'round',
      };
    }

    return {
      color: '#ffffff',
      weight: 0.8,
      opacity: 1,
      fillColor: '#00bdb9',
      fillOpacity: 1,
      lineJoin: 'round',
    };
  }

  function getCardDataForFeature(feature) {
    const iso = featureToIso(feature);
    if (countryCards && countryCards[iso]) return countryCards[iso];

    const featureName = normalizeText(getCountryName(feature));
    for (const [key, value] of Object.entries(countryCards || {})) {
      if (normalizeText(key) === featureName) return value;
    }
    return null;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getCardTags(cardData) {
    if (!Array.isArray(cardData?.tags)) return [];

    return cardData.tags.map((tag) => String(tag || '').trim()).filter(Boolean);
  }

  function createCardTagsHtml(tags) {
    if (!Array.isArray(tags) || !tags.length) return '';

    const visibleTags = tags.slice(0, 2);
    const overflowTags = tags.slice(2);
    const overflowCount = overflowTags.length;
    const overflowLabel = `${Math.min(overflowCount, 5)}+`;

    const visibleTagsHtml = visibleTags
      .map((tag) => `<span class="au-tag t-detail">${escapeHtml(tag)}</span>`)
      .join('');

    let overflowHtml = '';
    if (overflowCount > 0) {
      const overflowListHtml = overflowTags
        .map((tag) => `<li class="au-world-map__card-tags-tooltip-item">${escapeHtml(tag)}</li>`)
        .join('');

      overflowHtml = `
      <div class="au-world-map__card-tags-overflow">
        <button type="button" class="au-tag t-detail au-world-map__card-tags-counter" aria-label="${escapeHtml(formatText(texts.cardTagsOverflowLabel, { count: overflowCount }))}">${overflowLabel}</button>
        <ul class="au-world-map__card-tags-tooltip" role="list">
          ${overflowListHtml}
        </ul>
      </div>
    `;
    }

    return `
    <div class="au-world-map__card-tags" aria-label="${escapeHtml(texts.cardTagsLabel)}">
      ${visibleTagsHtml}
      ${overflowHtml}
    </div>
  `;
  }

  function getNoResultsToastHtml() {
    return `
  <p class="t-label">${escapeHtml(texts.noResultsTitle)}</p>
  <p class="t-detail">${escapeHtml(texts.noResultsDetail)}</p>
`;
  }

  function createCountryCardHtml(feature) {
    const cardData = getCardDataForFeature(feature);
    if (!cardData) return '';

    const title = cardData.title || getCountryName(feature) || texts.cardDefaultTitle;
    const description = cardData.description || texts.cardDefaultDescription;
    const image = String(cardData.image || '').trim();
    const link = sanitizeLink(cardData.link);
    const tags = getCardTags(cardData);
    const tagsHtml = createCardTagsHtml(tags);
    const imageHtml = image
      ? `<img class="au-world-map__card-image" src="${escapeHtml(image)}" alt="" loading="lazy" />`
      : '';

    return `
    <div class="au-world-map__card-container">
      ${imageHtml}
      <div class="au-world-map__card-body">
        <h3 class="au-world-map__card-title t-h4">${escapeHtml(title)}</h3>
        <p class="au-world-map__card-description t-label">${escapeHtml(description)}</p>
        ${tagsHtml}
        <a class="au-world-map__card-link" href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(formatText(texts.cardLinkLabel, { title }))}">
          <span class="material-symbols-rounded" aria-hidden="true" data-au-icon="">arrow_right_alt</span>
        </a>
      </div>
    </div>
  `;
  }

  function openCountryPopup(feature, latlng) {
    const countryIso = featureToIso(feature);
    const cardHtml = createCountryCardHtml(feature);
    if (!cardHtml) return;

    map?.setMaxBounds(null);

    const popup = L.popup({
      closeButton: true,
      autoPan: true,
      autoPanPaddingTopLeft: L.point(POPUP_SIDE_PADDING, POPUP_TOP_PADDING),
      autoPanPaddingBottomRight: L.point(POPUP_SIDE_PADDING, POPUP_SIDE_PADDING),
      keepInView: false,
      className: 'au-world-map__card',
      maxWidth: 360,
      closeOnClick: true,
    });
    popup._worldMapCountryIso = countryIso;
    popup.setLatLng(latlng).setContent(cardHtml).openOn(map);
  }

  function getLayerCenter(layer) {
    if (typeof layer.getBounds === 'function') return layer.getBounds().getCenter();
    if (typeof layer.getLatLng === 'function') return layer.getLatLng();
    return map?.getCenter();
  }

  function activateFeatureLayer(layer, feature, latlng) {
    popupOriginCountryIso = featureToIso(feature);

    layer.setStyle({
      fillColor: reliefEnabled ? 'transparent' : '#004656',
      color: reliefEnabled ? RELIEF_STROKE_HOVER_COLOR : '#ffffff',
      weight: reliefEnabled ? RELIEF_STROKE_HOVER_WEIGHT : 1.2,
      fillOpacity: reliefEnabled ? 0 : 1,
    });

    openCountryPopup(feature, latlng || getLayerCenter(layer));
  }

  function getActiveCountryElements() {
    const mapContainer = inRoot('[data-au-world-map-map]');
    if (!mapContainer) return [];

    return Array.from(mapContainer.querySelectorAll('.leaflet-overlay-pane path[data-au-world-map-iso][tabindex="0"]'));
  }

  function focusCountryByIso(iso) {
    if (!iso) return;

    const countryElements = getActiveCountryElements();
    const nextElement = countryElements.find((element) => element.getAttribute('data-au-world-map-iso') === iso);

    if (!nextElement) return;

    requestAnimationFrame(() => {
      nextElement.focus({ preventScroll: true });
    });
  }

  function getPopupFocusableElements(popupElement) {
    if (!popupElement) return [];

    const orderedSelectors = [
      '.au-world-map__card-button',
      '.au-world-map__card-tags-counter',
      '.au-world-map__card-link',
    ];

    return orderedSelectors
      .flatMap((selector) => Array.from(popupElement.querySelectorAll(selector)))
      .filter((element) => !element.hasAttribute('disabled') && element.tabIndex !== -1);
  }

  function bindPopupKeyboardNavigation(popupElement) {
    if (!popupElement || popupElement.dataset.worldMapPopupKeyboardBound === 'true') return;

    popupElement.dataset.worldMapPopupKeyboardBound = 'true';
    const originIso = popupOriginCountryIso;
    const popupWrapper = popupElement.closest('.leaflet-popup') || popupElement;

    popupWrapper.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          pendingCountryFocusIso = originIso;
          map.closePopup();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusable = getPopupFocusableElements(popupElement);
        if (!focusable.length) return;

        event.preventDefault();
        event.stopPropagation();

        const currentIndex = focusable.indexOf(document.activeElement);
        const direction = event.shiftKey ? -1 : 1;
        const nextIndex =
          currentIndex === -1
            ? event.shiftKey
              ? focusable.length - 1
              : 0
            : (currentIndex + direction + focusable.length) % focusable.length;

        focusElementWithRetry(focusable[nextIndex]);
      },
      true,
    );
  }

  function focusElementWithRetry(element, attempts = 4) {
    if (!element || attempts <= 0) return;

    element.focus();
    if (document.activeElement === element) return;

    requestAnimationFrame(() => {
      focusElementWithRetry(element, attempts - 1);
    });
  }

  function rectsOverlap(a, b) {
    return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
  }

  function refreshControlsOverlapState() {
    const controls = controlsEl();
    if (!controls) return;

    const openPopup = inRoot('.leaflet-popup.au-world-map__card');
    const overlaps = !!openPopup && rectsOverlap(openPopup.getBoundingClientRect(), controls.getBoundingClientRect());
    controls.classList.toggle('au-world-map__controls--card-open', overlaps);
  }

  function bindPopupLifecycleEvents() {
    if (!map || map._worldMapPopupEventsBound) return;

    map._worldMapPopupEventsBound = true;

    map.on('move zoom', refreshControlsOverlapState);

    map.on('popupopen', (event) => {
      const popupElement = event.popup?.getElement();
      if (!popupElement) return;

      const cardTitle = getPopupCardTitle(popupElement);
      const countryIso = event.popup?._worldMapCountryIso;

      const dialogWrapper = popupElement.closest('.leaflet-popup') || popupElement;
      dialogWrapper.setAttribute('role', 'dialog');
      dialogWrapper.setAttribute('aria-modal', 'false');
      const titleEl = popupElement.querySelector('.au-world-map__card-title');
      if (titleEl) {
        titleEl.id = titleEl.id || `au-wm-card-title-${countryIso}`;
        dialogWrapper.setAttribute('aria-labelledby', titleEl.id);
      }

      // Actualizar aria-expanded del país que abrió el popup
      if (countryIso) {
        const mapContainer = inRoot('[data-au-world-map-map]');
        if (mapContainer) {
          const countryElement = mapContainer.querySelector(`[data-au-world-map-iso="${countryIso}"]`);
          if (countryElement) {
            countryElement.setAttribute('aria-expanded', 'true');
          }
        }
      }

      const popupWrapper = popupElement.closest('.leaflet-popup');
      const popupTipContainer = popupWrapper?.querySelector('.leaflet-popup-tip-container');
      if (popupTipContainer) {
        popupTipContainer.remove();
      }

      const closeAnchor = popupElement.querySelector('.leaflet-popup-close-button');
      if (closeAnchor instanceof HTMLAnchorElement) {
        const closeButtonElement = document.createElement('button');
        const label =
          closeAnchor.getAttribute('aria-label') || closeAnchor.getAttribute('title') || texts.cardCloseLabel;

        closeButtonElement.type = 'button';
        closeButtonElement.className =
          'au-world-map__card-button au-button au-button--secondary au-button--small au-button--icon-only';
        closeButtonElement.innerHTML = '<span class="material-symbols-rounded" aria-hidden="true">close</span>';
        closeButtonElement.setAttribute('aria-label', label);

        const title = closeAnchor.getAttribute('title');
        if (title) closeButtonElement.setAttribute('title', title);

        closeAnchor.replaceWith(closeButtonElement);

        const cardContainer = popupElement.querySelector('.au-world-map__card-container');
        if (cardContainer && !cardContainer.contains(closeButtonElement)) {
          cardContainer.prepend(closeButtonElement);
        }
      }

      const closeButton = popupElement.querySelector('.au-world-map__card-button');
      if (closeButton && closeButton.dataset.worldMapCloseBound !== 'true') {
        closeButton.dataset.worldMapCloseBound = 'true';
        closeButton.addEventListener('click', (clickEvent) => {
          pushPopupCardButtonDataLayer(cardTitle, 'cerrar');
          if (clickEvent.detail === 0 && countryIso) {
            pendingCountryFocusIso = countryIso;
          }
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          map.closePopup();
        });
      }

      const cardLink = popupElement.querySelector('.au-world-map__card-link');
      if (cardLink && cardLink.dataset.worldMapLinkDataLayerBound !== 'true') {
        cardLink.dataset.worldMapLinkDataLayerBound = 'true';
        cardLink.addEventListener('click', () => {
          pushPopupCardButtonDataLayer(cardTitle, 'Flecha');
        });
      }

      bindPopupKeyboardNavigation(popupElement);

      refreshControlsOverlapState();

      const focusable = getPopupFocusableElements(popupElement);
      if (focusable.length) {
        focusElementWithRetry(focusable[0]);
      }
    });

    map.on('popupclose', (event) => {
      map?.setMaxBounds(WORLD_MAP_BOUNDS);

      const controlsPanel = controlsEl();
      if (controlsPanel) {
        controlsPanel.classList.remove('au-world-map__controls--card-open');
      }

      const countryIso = event.popup?._worldMapCountryIso;
      if (countryIso && popupOriginCountryIso === countryIso) {
        popupOriginCountryIso = '';
      }

      if (countryIso) {
        // Actualizar aria-expanded del país
        const mapContainer = inRoot('[data-au-world-map-map]');
        if (mapContainer) {
          const countryElement = mapContainer.querySelector(`[data-au-world-map-iso="${countryIso}"]`);
          if (countryElement) {
            countryElement.setAttribute('aria-expanded', 'false');
          }
        }
        // Resetear estilos del país
        if (activeCountryLayers.has(countryIso)) {
          const layerData = activeCountryLayers.get(countryIso);
          if (layerData && layerData.layer && layerData.feature) {
            const style = getStyleForFeature(layerData.feature);
            layerData.layer.setStyle(style);
          }
        }
      }

      if (pendingCountryFocusIso) {
        const focusIso = pendingCountryFocusIso;
        pendingCountryFocusIso = '';
        focusCountryByIso(focusIso);
        return;
      }
    });
  }

  function bindMapKeyboardActivation() {
    const mapContainer = inRoot('[data-au-world-map-map]');
    if (!mapContainer || mapContainer.dataset.worldMapKeyboardDelegateBound === 'true') return;

    mapContainer.dataset.worldMapKeyboardDelegateBound = 'true';
    mapContainer.addEventListener('keydown', (event) => {
      const target = event.target;
      if (!(target instanceof SVGElement)) return;

      const featureIso = target.getAttribute('data-au-world-map-iso');
      if (!featureIso || !activeCountryLayers.has(featureIso)) return;

      if (event.key !== 'Enter' && event.key !== ' ') return;

      const activeFeature = activeCountryLayers.get(featureIso);
      if (!activeFeature) return;

      event.preventDefault();
      pushCountrySelectionDataLayer(activeFeature.feature);
      activateFeatureLayer(activeFeature.layer, activeFeature.feature);
    });
  }

  function calculateBoundsFromSelectedCountries() {
    if (!map || selectedCountries.size === 0 || !countriesGeojson?.features) return null;

    if (selectedCountriesBoundsCache) return selectedCountriesBoundsCache;

    let bounds = null;

    countriesGeojson.features.forEach((feature) => {
      const iso = featureToIso(feature);
      if (!selectedCountries.has(iso)) return;

      try {
        const layer = L.geoJSON(feature);
        const featureBounds = layer.getBounds();

        if (!bounds) {
          bounds = featureBounds;
        } else {
          bounds.extend(featureBounds);
        }
      } catch {
        // Si hay error procesando la feature, continúa
      }
    });

    selectedCountriesBoundsCache = bounds;
    return bounds;
  }

  function applyInitialView() {
    // Si no hay región especificada, adaptar al viewport de los países seleccionados
    if (!region) {
      const selectedCountriesBounds = calculateBoundsFromSelectedCountries();
      if (selectedCountriesBounds) {
        map.fitBounds(selectedCountriesBounds, {
          padding: [20, 20],
          maxZoom: 5,
        });
        return;
      }
      // Si no hay países, mostrar el mundo
      map.setView([20, 0], 2);
      return;
    }

    // Si hay región especificada, usar sus bounds
    const regionDef = regions[region] || regions.world;

    if (regionDef && regionDef.bounds) {
      map.fitBounds(regionDef.bounds, {
        padding: [20, 20],
        maxZoom: regionDef.maxZoom || 5,
      });
      return;
    }

    map.setView([20, 0], 2);
  }

  function updateCategoryButtonIcon(btn, isSelected, originalIcon) {
    const iconSpan = btn.querySelector('[data-au-icon]');
    if (!iconSpan) return;

    if (isSelected) {
      // Swap to check icon (Material Symbols)
      iconSpan.className = 'material-symbols-rounded';
      iconSpan.textContent = 'check';
    } else {
      // Swap back to original brand icon
      iconSpan.className = `icon icon-${originalIcon}`;
      iconSpan.textContent = '';
    }
  }

  function updateClearFiltersChipVisibility() {
    const clearFiltersSlide = clearFiltersSlideEl();
    const clearFiltersChip = clearFiltersChipEl();
    if (!clearFiltersSlide || !clearFiltersChip) return;

    const hasSelectedCategories = selectedCategories.size > 0;

    if (hasSelectedCategories) {
      clearFiltersSlide.classList.remove('au-hide');
      void clearFiltersSlide.offsetWidth;
      clearFiltersSlide.classList.add('au-world-map__filters-clear');
      clearFiltersChip.removeAttribute('tabindex');
      return;
    }

    clearFiltersSlide.classList.add('au-hide');
    clearFiltersSlide.classList.remove('au-world-map__filters-clear');
    clearFiltersChip.setAttribute('tabindex', '-1');
  }

  function clearAllCategoryFilters() {
    if (selectedCategories.size === 0) return;

    const previouslySelectedCategories = Array.from(selectedCategories);
    selectedCategories.clear();

    previouslySelectedCategories.forEach((norm) => {
      pushCategoryFilterDataLayer(norm, 'deseleccionar');
    });

    renderCategoryButtons();
    rebuildLayers();
  }

  function bindClearFiltersChip() {
    const clearFiltersChip = clearFiltersChipEl();
    if (!clearFiltersChip || clearFiltersChip.dataset.worldMapBound === 'true') return;

    clearFiltersChip.dataset.worldMapBound = 'true';
    clearFiltersChip.setAttribute('aria-label', texts.clearFiltersLabel);
    clearFiltersChip.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    clearFiltersChip.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      requestAnimationFrame(() => {
        clearAllCategoryFilters();
      });
    });

    clearFiltersChip.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopPropagation();
      clearAllCategoryFilters();
    });
  }

  function renderCategoryButtons() {
    const cats = getConfiguredCategories(categories, countryCategories);
    const configuredKeys = new Set(cats.map((category) => category.key));
    const buttonsRoot = filtersEl();

    if (!buttonsRoot) return;

    buttonsRoot.querySelectorAll('[data-au-world-map-category]').forEach((btn) => {
      const key = btn.dataset.auWorldMapCategoryKey || '';
      const isConfigured = configuredKeys.has(key);

      btn.style.display = isConfigured ? '' : 'none';
      if (!isConfigured) return;

      const isSelected = selectedCategories.has(key);
      const originalIcon = categoryByKey.get(key)?.icon || 'info';

      btn.classList.toggle('au-active', isSelected);
      updateCategoryButtonIcon(btn, isSelected, originalIcon);

      if (btn.dataset.worldMapBound === 'true') return;
      btn.dataset.worldMapBound = 'true';
      btn.addEventListener('click', () => {
        const isSelecting = !selectedCategories.has(key);
        if (isSelecting) selectedCategories.add(key);
        else selectedCategories.delete(key);

        pushCategoryFilterDataLayer(key, isSelecting ? 'seleccionar' : 'deseleccionar');

        renderCategoryButtons();
        rebuildLayers();
      });
    });

    bindClearFiltersChip();
    updateClearFiltersChipVisibility();
    syncOthersSelectFromState();
    initFiltersSwiper();
    filtersSwiper?.update();
  }

  function bindOverlayEventIsolation() {
    [filtersEl(), controlsEl()].forEach((element) => {
      if (!element || element.dataset.worldMapLeafletBound === 'true') return;

      L.DomEvent.disableClickPropagation(element);
      L.DomEvent.disableScrollPropagation(element);
      element.dataset.worldMapLeafletBound = 'true';
    });
  }

  function initReliefLayer() {
    if (!map || !reliefEnabled) return;

    const reliefPane = map.createPane(RELIEF_PANE_NAME);
    reliefPane.style.zIndex = '250';
    reliefPane.style.pointerEvents = 'none';
    reliefPane.style.filter = RELIEF_PANE_FILTER;

    reliefLayer = L.tileLayer(RELIEF_TILE_URL, {
      attribution: RELIEF_TILE_ATTRIBUTION,
      maxZoom: 8,
      opacity: RELIEF_TILE_OPACITY,
      pane: RELIEF_PANE_NAME,
    }).addTo(map);

    reliefLayer.on('tileerror', () => {
      if (reliefFallbackApplied) return;

      reliefFallbackApplied = true;
      map.removeLayer(reliefLayer);
      reliefLayer = L.tileLayer(RELIEF_TILE_FALLBACK_URL, {
        attribution: RELIEF_TILE_FALLBACK_ATTRIBUTION,
        maxZoom: 8,
        opacity: RELIEF_TILE_OPACITY,
        pane: RELIEF_PANE_NAME,
      }).addTo(map);
    });
  }

  function ensureFocusVisibleFilter() {
    const mapContainer = inRoot('[data-au-world-map-map]');
    const svg = mapContainer?.querySelector('.leaflet-overlay-pane svg');

    if (!svg || svg.querySelector(`#${focusFilterId}`)) return;

    const svgNs = 'http://www.w3.org/2000/svg';
    const defs = svg.querySelector('defs') || document.createElementNS(svgNs, 'defs');

    if (!defs.parentNode) {
      svg.prepend(defs);
    }

    const filter = document.createElementNS(svgNs, 'filter');
    filter.setAttribute('id', focusFilterId);

    /*
     * Se amplía la región porque el halo azul sale fuera de los límites
     * originales del path. Sin esto puede aparecer recortado.
     */
    filter.setAttribute('x', '-30%');
    filter.setAttribute('y', '-30%');
    filter.setAttribute('width', '160%');
    filter.setAttribute('height', '160%');
    filter.setAttribute('color-interpolation-filters', 'sRGB');

    const morphology = document.createElementNS(svgNs, 'feMorphology');
    morphology.setAttribute('in', 'SourceAlpha');
    morphology.setAttribute('operator', 'dilate');
    morphology.setAttribute('radius', '1');
    morphology.setAttribute('result', 'focus-expanded');

    const flood = document.createElementNS(svgNs, 'feFlood');
    flood.setAttribute('flood-color', '#006BFF');
    flood.setAttribute('result', 'focus-blue');

    const composite = document.createElementNS(svgNs, 'feComposite');
    composite.setAttribute('in', 'focus-blue');
    composite.setAttribute('in2', 'focus-expanded');
    composite.setAttribute('operator', 'in');
    composite.setAttribute('result', 'focus-outline');

    const merge = document.createElementNS(svgNs, 'feMerge');

    const outlineNode = document.createElementNS(svgNs, 'feMergeNode');
    outlineNode.setAttribute('in', 'focus-outline');

    const originalPathNode = document.createElementNS(svgNs, 'feMergeNode');
    originalPathNode.setAttribute('in', 'SourceGraphic');

    /*
     * Primero azul, después el path original:
     * fill → stroke blanco original → borde azul exterior.
     */
    merge.append(outlineNode, originalPathNode);

    filter.append(morphology, flood, composite, merge);
    defs.append(filter);
  }

  function rebuildLayers() {
    if (!map || !worldData) return;

    if (baseLayer) map.removeLayer(baseLayer);
    activeCountryLayers = new Map();

    baseLayer = L.geoJSON(worldData, {
      style: (feature) => getStyleForFeature(feature),
      onEachFeature: (feature, layer) => {
        const active = isActiveFeature(feature);

        if (!active) {
          layer.options.interactive = false;
          layer.on('add', () => {
            const el = layer.getElement();
            if (!el) return;
            el.style.cursor = 'default';
            el.tabIndex = -1;
            el.setAttribute('tabindex', '-1');
            el.setAttribute('aria-hidden', 'true');
            el.removeAttribute('data-au-world-map-iso');
            el.removeAttribute('aria-keyshortcuts');
          });
          return;
        }

        const featureIso = featureToIso(feature);
        activeCountryLayers.set(featureIso, { layer, feature });

        layer.on('add', () => {
          const el = layer.getElement();
          if (!el) return;

          const hasCard = getCardDataForFeature(feature);
          const countryName = hasCard?.title || getCountryName(feature) || texts.activeCountryFallbackLabel;
          el.style.cursor = 'pointer';
          el.tabIndex = 0;
          el.setAttribute('tabindex', '0');
          el.setAttribute('focusable', 'true');
          el.setAttribute('role', 'button');
          el.setAttribute('data-au-world-map-iso', featureIso);
          el.setAttribute('aria-keyshortcuts', 'Enter');
          el.setAttribute('aria-label', countryName);
          el.style.setProperty('--au-world-map-focus-filter', `url("#${focusFilterId}")`);
          if (hasCard) {
            el.setAttribute('aria-haspopup', 'dialog');
            el.setAttribute('aria-expanded', 'false');
          }
          el.removeAttribute('aria-hidden');
        });

        layer.on('mouseover', () => {
          layer.setStyle({
            color: reliefEnabled ? RELIEF_STROKE_HOVER_COLOR : '#ffffff',
            weight: reliefEnabled ? RELIEF_STROKE_HOVER_WEIGHT : 1.2,
            fillColor: reliefEnabled ? 'transparent' : '#0478b2',
            fillOpacity: reliefEnabled ? 0 : 0.95,
          });
        });

        layer.on('mouseout', () => {
          // No resetear si hay un popup abierto para este país
          if (popupOriginCountryIso === featureIso) {
            return;
          }
          baseLayer.resetStyle(layer);
        });

        layer.on('click', (e) => {
          pushCountrySelectionDataLayer(feature);
          activateFeatureLayer(layer, feature, e.latlng);
        });
      },
    }).addTo(map);

    ensureFocusVisibleFilter();
    applyInitialView();

    const activeCount = Array.from(worldData.features || []).filter(isActiveFeature).length;
    if (activeCount === 0 && selectedCategories.size > 0) {
      showToast(getNoResultsToastHtml(), 0, true);
    } else {
      toastEl()?.classList.remove('au-show');
    }
  }

  function shouldCreateFiltersSwiper(filtersElement) {
    if (!filtersElement) return false;

    const filtersWrapper = filtersElement.querySelector('.swiper-wrapper');
    if (!filtersWrapper) return false;

    return filtersWrapper.scrollWidth - filtersElement.clientWidth > 1;
  }

  function updateFiltersSliderFades(swiper) {
    const el = swiper.el;
    if (!el) return;

    if (swiper.isLocked) {
      el.classList.remove('au-world-map__filters--fade-left', 'au-world-map__filters--fade-right');
      return;
    }

    el.classList.toggle('au-world-map__filters--fade-left', !swiper.isBeginning);
    el.classList.toggle('au-world-map__filters--fade-right', !swiper.isEnd);
  }

  function createFiltersSwiper(filtersElement) {
    if (filtersSwiper) return;

    filtersSwiper = new Swiper(filtersElement, {
      modules: [Navigation, Scrollbar, FreeMode, A11y],
      ...sliderConfig,
      spaceBetween: 8,
      freeMode: true,
      slidesPerView: 'auto',
      on: {
        ...sliderConfig.on,
        init() {
          updateFiltersSliderFades(this);
        },
        slideChange() {
          updateFiltersSliderFades(this);
        },
        setTranslate() {
          updateFiltersSliderFades(this);
        },
        reachBeginning() {
          updateFiltersSliderFades(this);
        },
        reachEnd() {
          updateFiltersSliderFades(this);
        },
        fromEdge() {
          updateFiltersSliderFades(this);
        },
        resize() {
          updateFiltersSliderFades(this);
        },
        update() {
          updateFiltersSliderFades(this);
        },
      },
    });

    filtersElement._swiper = filtersSwiper;
    updateFiltersSliderFades(filtersSwiper);
  }

  function destroyFiltersSwiper() {
    if (!filtersSwiper) return;

    const filtersElement = filtersSwiper.el || filtersEl();
    filtersSwiper.destroy();
    filtersSwiper = null;

    filtersElement?.classList.remove('au-world-map__filters--fade-left', 'au-world-map__filters--fade-right');
  }

  function initFiltersSwiper() {
    const filtersElement = filtersEl();
    if (!filtersElement) return;

    if (shouldCreateFiltersSwiper(filtersElement)) {
      createFiltersSwiper(filtersElement);
    } else {
      destroyFiltersSwiper();
    }
  }

  function locateMe() {
    if (!navigator.geolocation || !map) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;

        map.setView([latitude, longitude], Math.max(map.getZoom(), 6), {
          animate: true,
        });
        showToast(texts.locateSuccess);
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  function resetLeafletContainer(mapContainer) {
    if (!mapContainer) return;

    mapContainer
      .querySelectorAll(
        '.leaflet-pane, .leaflet-control-container, .leaflet-map-pane, .leaflet-proxy, .leaflet-zoom-box',
      )
      .forEach((node) => node.remove());

    mapContainer.classList.remove(
      'leaflet-container',
      'leaflet-touch',
      'leaflet-retina',
      'leaflet-fade-anim',
      'leaflet-grab',
      'leaflet-touch-drag',
      'leaflet-touch-zoom',
    );
    mapContainer.removeAttribute('style');
    if (mapContainer._leaflet_id) delete mapContainer._leaflet_id;
  }

  function alignCustomControlsInLeafletDom() {
    const mapContainer = inRoot('[data-au-world-map-map]');
    if (!mapContainer) return;

    const leafletControlContainer = mapContainer.querySelector('.leaflet-control-container');
    if (leafletControlContainer) {
      leafletControlContainer.remove();
    }

    const customControls = controlsEl();
    const leafletMapPane = mapContainer.querySelector('.leaflet-pane.leaflet-map-pane');

    if (!customControls || !leafletMapPane) return;

    if (leafletMapPane.nextElementSibling !== customControls) {
      leafletMapPane.insertAdjacentElement('afterend', customControls);
    }
  }

  async function init(config = {}, rootElement = null) {
    rootEl = rootElement instanceof HTMLElement ? rootElement : document.querySelector('[data-au-world-map]');
    if (!rootEl) return;

    cleanupGlobalListeners();

    if (othersSelectObserver) {
      othersSelectObserver.disconnect();
      othersSelectObserver = null;
    }

    if (map) {
      map.remove();
      map = null;
      baseLayer = null;
      reliefLayer = null;
    }

    reliefFallbackApplied = false;

    pendingCountryFocusIso = '';

    const mapContainer = inRoot('[data-au-world-map-map]');
    if (!mapContainer) return;
    resetLeafletContainer(mapContainer);

    categories = config.categories || categories;
    region = config.region ?? null;
    selectedColor = config.selectedColor || selectedColor;
    countryCategories = config.countryCategories || countryCategories;
    countryCards = config.countryCards || countryCards;
    regions = {
      ...DEFAULT_REGIONS,
      ...(config.regions || {}),
    };
    rebuildCategoryLookups();
    texts = { ...DEFAULT_TEXTS, ...(config.texts || {}) };
    reliefEnabled = rootEl.hasAttribute('data-au-world-map-relief');

    selectedCountries = normalizeSelectedCountries(config.countries || []);
    selectedCountriesBoundsCache = null;
    selectedCategories = new Set(
      (Array.isArray(config.selectedCategories) ? config.selectedCategories : [])
        .map((category) => String(category ?? '').trim())
        .filter(Boolean),
    );

    const mapOptions = {
      zoomControl: false,
      attributionControl: false,
      keyboard: false,
      dragging: true,
      boxZoom: true,
      doubleClickZoom: true,
      minZoom: 2,
      maxZoom: 10,
      worldCopyJump: true,
      maxBounds: WORLD_MAP_BOUNDS,
      maxBoundsViscosity: 0.85,
      preferCanvas: false,
      renderer: L.svg(),
      scrollWheelZoom: false,
      tap: false,
    };

    map = L.map(mapContainer, mapOptions);

    map.dragging.enable();
    map.doubleClickZoom.enable();
    map.boxZoom.enable();
    map.touchZoom.enable();

    alignCustomControlsInLeafletDom();

    initReliefLayer();

    countriesGeojson = await loadCountriesGeojson();
    worldData = countriesGeojson;
    buildCountryMappingFromGeojson();

    bindOverlayEventIsolation();
    bindMapKeyboardActivation();
    bindPopupLifecycleEvents();
    bindOthersSelectEvents();
    renderCategoryButtons();
    rebuildLayers();
    initFiltersSwiper();

    const zoomInBtn = inRoot('[data-au-world-map-zoom-in]');
    const zoomOutBtn = inRoot('[data-au-world-map-zoom-out]');
    const locateBtn = inRoot('[data-au-world-map-locate]');

    if (zoomInBtn) {
      zoomInBtn.onclick = () => {
        pushControlButtonDataLayer('zoom ampliar');
        map.zoomIn();
      };
    }

    if (zoomOutBtn) {
      zoomOutBtn.onclick = () => {
        pushControlButtonDataLayer('zoom reducir');
        map.zoomOut();
      };
    }

    if (locateBtn) {
      locateBtn.onclick = () => {
        pushControlButtonDataLayer('ubicacion');
        locateMe();
      };
    }

    resizeHandler = () => {
      initFiltersSwiper();
    };
    window.addEventListener('resize', resizeHandler);
  }

  function destroy() {
    cleanupGlobalListeners();

    if (othersSelectObserver) {
      othersSelectObserver.disconnect();
      othersSelectObserver = null;
    }

    destroyFiltersSwiper();

    if (toastTimer) {
      clearTimeout(toastTimer);
      toastTimer = null;
    }

    if (map) {
      map.remove();
      map = null;
      baseLayer = null;
      reliefLayer = null;
    }

    activeCountryLayers = new Map();
    rootEl = null;
    worldData = null;
    countriesGeojson = null;
  }

  return { init, destroy };
}

const worldMapInstances = new WeakMap();
const WORLD_MAP_SELECTOR = '[data-au-world-map]';

function resolveRootElements(rootOrSelector) {
  if (rootOrSelector instanceof HTMLElement) {
    return [rootOrSelector];
  }

  if (Array.isArray(rootOrSelector)) {
    return rootOrSelector.filter((element) => element instanceof HTMLElement);
  }

  if (typeof rootOrSelector === 'string') {
    return Array.from(document.querySelectorAll(rootOrSelector));
  }

  return Array.from(document.querySelectorAll(WORLD_MAP_SELECTOR));
}

function getOrCreateInstance(rootElement) {
  let instance = worldMapInstances.get(rootElement);
  if (!instance) {
    instance = createWorldMapInstance();
    worldMapInstances.set(rootElement, instance);
  }
  return instance;
}

async function init(config = {}, rootOrSelector = null) {
  const rootElements = resolveRootElements(rootOrSelector);
  if (!rootElements.length) return;

  const configList = Array.isArray(config) ? config : rootElements.map(() => config);

  await Promise.all(
    rootElements.map((rootElement, index) => {
      const instanceConfig = configList[index] || configList[0] || {};
      return getOrCreateInstance(rootElement).init(instanceConfig, rootElement);
    }),
  );
}

function destroy(rootOrSelector = null) {
  resolveRootElements(rootOrSelector).forEach((rootElement) => {
    const instance = worldMapInstances.get(rootElement);
    if (!instance) return;

    instance.destroy();
    worldMapInstances.delete(rootElement);
  });
}

export default { init, destroy };
